import Dialog from "../../dialog/Dialog";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  STATUS_DIALOG_WIDTH,
  StatusApprovalSection,
  StatusSettingsBody,
  StatusToggleRow,
} from "../../common/StatusSettingsParts";
import { packageSettings } from "./packageService";

function to01(value) {
  if (value === true || value === 1 || value === "1") return 1;
  return 0;
}

function toApprovalTriState(value) {
  if (value === true || value === 1 || value === "1") return 1;
  if (value === 2 || value === "2") return 2;
  return 0;
}

const PackageSettings = ({ open, onClose, packageData, onNotify }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState({
    popular: to01(packageData?.popular),
    is_approved: toApprovalTriState(packageData?.is_approved),
    status: to01(packageData?.status),
    recommended: to01(packageData?.recommended),
  });

  useEffect(() => {
    if (!open || !packageData) return;
    setStatus({
      popular: to01(packageData.popular),
      is_approved: toApprovalTriState(packageData.is_approved),
      status: to01(packageData.status),
      recommended: to01(packageData.recommended),
    });
  }, [open, packageData]);

  const handleToggle = (field) => {
    setStatus((prev) => ({
      ...prev,
      [field]: prev[field] === 1 ? 0 : 1,
    }));
  };

  const updatePackageSettingsMutation = useMutation({
    mutationFn: () => packageSettings(packageData.id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(["packages"]);
      onNotify?.({
        message: "Package settings updated successfully",
        type: "success",
      });
      onClose();
    },
    onError: (error) => {
      const apiMessage = error.response?.data?.message;
      const validationErrors = error.response?.data?.errors;
      const message =
        (typeof apiMessage === "string" && apiMessage) ||
        (validationErrors &&
          Object.values(validationErrors).flat().join(", ")) ||
        error.message ||
        "Failed to update package settings";

      onNotify?.({
        message,
        type: "error",
      });
    },
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Package Status"
        width={STATUS_DIALOG_WIDTH}
        primaryButtonText="Save Changes"
        primaryButtonAction={() => updatePackageSettingsMutation.mutate()}
        isLoading={updatePackageSettingsMutation.isPending}
      >
        <StatusSettingsBody>
          <StatusToggleRow
            title="Popular Package"
            description="Show this package as popular"
            checked={status.popular === 1}
            onChange={() => handleToggle("popular")}
          />
          <StatusToggleRow
            title="Explore World Recommended"
            description="Mark this package as recommended by Explore World"
            checked={status.recommended === 1}
            onChange={() => handleToggle("recommended")}
          />
          <StatusApprovalSection
            value={status.is_approved}
            onChange={(value) =>
              setStatus((prev) => ({ ...prev, is_approved: value }))
            }
            description="Set the approval status for this package"
          />
          <StatusToggleRow
            title="Active Status"
            description="Package is active and visible to users"
            checked={status.status === 1}
            onChange={() => handleToggle("status")}
          />
        </StatusSettingsBody>
      </Dialog>
    </>
  );
};

export default PackageSettings;
